// Drives the event page's race tabs (ViewComponents::Navs::EventDistances):
// one row of tabs that scrolls sideways when the races do not fit.
//
// On load the active tab is scrolled to the middle of the row, so a race picked
// from the far end is still in view on its own page. While the row overflows,
// the arrows show (each one disabled at its end) and each edge with more tabs
// past it fades (race_tabs.scss). Each tab is a plain link to its race's page,
// so nothing here changes which race is shown.

// How far one arrow click moves the row, as a share of its visible width: most
// of a screen, with some tabs kept in view for context.
const SCROLL_SHARE = 0.7;

function wireTabs(root) {
  const rail = root.querySelector('[data-race-tabs-rail]');
  const prev = root.querySelector('[data-race-tabs-scroll="prev"]');
  const next = root.querySelector('[data-race-tabs-scroll="next"]');
  if (!rail || !prev || !next) return;

  const updateEnds = () => {
    const scrolls = root.classList.contains('race-tabs--scrolls');
    const max = rail.scrollWidth - rail.clientWidth;
    const atStart = rail.scrollLeft <= 1;
    const atEnd = rail.scrollLeft >= max - 1;

    prev.disabled = atStart;
    next.disabled = atEnd;
    root.classList.toggle('race-tabs--more-before', scrolls && !atStart);
    root.classList.toggle('race-tabs--more-after', scrolls && !atEnd);
  };

  // Measured with the arrows hidden: they take room from the row, so a row
  // measured with them shown can look like it overflows when it would fit.
  const measure = () => {
    prev.hidden = true;
    next.hidden = true;
    root.classList.remove('race-tabs--scrolls');

    const scrolls = rail.scrollWidth > rail.clientWidth + 1;
    prev.hidden = !scrolls;
    next.hidden = !scrolls;
    root.classList.toggle('race-tabs--scrolls', scrolls);
    updateEnds();
  };

  const active = rail.querySelector('[aria-current="page"]');

  const centerActive = () => {
    if (!active) return;

    const left = active.getBoundingClientRect().left - rail.getBoundingClientRect().left;
    rail.scrollLeft += left - (rail.clientWidth - active.offsetWidth) / 2;
  };

  const scrollBy = direction => () => {
    rail.scrollBy({ left: direction * rail.clientWidth * SCROLL_SHARE, behavior: 'smooth' });
  };

  prev.addEventListener('click', scrollBy(-1));
  next.addEventListener('click', scrollBy(1));
  rail.addEventListener('scroll', updateEnds, { passive: true });

  // Re-centred on every resize until the visitor moves the row themselves.
  let moved = false;
  ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(type => {
    root.addEventListener(type, () => { moved = true; }, { passive: true });
  });
  const settle = () => {
    measure();
    if (!moved) centerActive();
    updateEnds();
  };

  // The wrapper follows the window. The active tab widens when the web font
  // arrives, which can be after the page's load event, and that pushes it off
  // the row again. Not the rail: its own width changes as the arrows show and
  // hide, which would call settle again from inside settle.
  if ('ResizeObserver' in window) {
    const observer = new ResizeObserver(settle);
    observer.observe(root);
    if (active) observer.observe(active);
  } else {
    window.addEventListener('resize', settle);
    window.addEventListener('load', settle);
  }

  settle();
}

export function init() {
  document.querySelectorAll('[data-race-tabs]').forEach(wireTabs);
}

// Run as soon as the DOM is ready — and immediately if it already is, since an
// appended pack can execute after DOMContentLoaded has fired.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
